import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { animateScroll } from 'react-scroll';

import {
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  FormGroup,
  Label,
  Row,
  TabContent,
  TabPane,
  Alert
} from 'reactstrap';
import CredsModal from '../Vendor/CredsModal';
import SummaryModal from '../Vendor/Modal';

import TabNav from '../section/TabNav';
import TabOne from '../section/TabOne';
import BZAPIService from '../../../services/BZAPIService';
import DefaultConfigTab from '../section/DefaultConfigTab';
import ProductPriority from '../section/ProductPriority';
import ExecutionStatsTab from '../section/ExecutionStatsTab';
import { useScheduling } from '../../../hooks/useScheduling';
// constants
import SETTINGS from '../../../constants/settings';
import ButtonIcon from '../../common/ButtonIcon';
import { FieldGroup } from '../../../libs/formLib';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ConfigFormAwsSecurityHub = ({
  vendor,
  isReadOnly,
  vendorLabel,
  bugsDateBack,
  setBugsDateBack,
  vendorId,
  vendorPriorityOptions,
  vendorStatusOptions,
  vendorResolutionOptions,
  snIntegrationCheck,
  services,
  vendors,
  priorityStats,
  setPriorityStats,
  snCompanySysIdOptions,
  snCompanySysIdErrors,
  vendorResolutions,
  setVendorResolutions,
  setVendorStatuses,
  vendorStatuses,
  fields,
  handleFieldChange,
  priorityMaps,
  setPriorityMaps,
  snPriorityOptions,
  keywords,
  setKeywords,
  searchFields,
  setSearchFields,
  snCompanySysId,
  setSnCompanySysId,
  setServices,
  setVendors,
  buildVendorStat,
  setBugs,
  isConfigured,
  syncOnly,
  setSyncOnly,
  loading,
  isServiceActive,
  setIsServiceActive,
  vendorBugCategories,
  setVendorBugCategories,
  vendorBugTypeOptions,
  vendorBugTypes,
  setVendorBugTypes,
  vendorImpactedRegions,
  setVendorImpactedRegions,
  vendorImpactedRegionOptions,
  vendorComplianceStatus,
  setVendorComplianceStatus,
  vendorComplianceStatusOptions,
  allRequired,
  setAllRequired,
  isUpdated,
  autoCloseBugTicket,
  setAutoCloseBugTicket,
  setIsUpdated
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [isValidCredentials, setIsValidCredentials] = useState(false);
  const [isTestingValidCredentials, setIsTestingValidCredentials] = useState(false);
  const [isAddingCredentials, setIsAddingCredentials] = useState(false);
  const [isUpdatingCreds, setIsUpdatingCreds] = useState(false);

  const [activeTab, setActiveTab] = useState('1');
  const [modalOpen, setModalOpen] = useState(false);
  const [validated, setValidated] = useState(null);
  const [isForceRun, setIsForceRun] = useState(false);

  // const [orgHealthError, setOrgHealthError] = useState();

  // Use scheduling hook for state and save function
  const {
    timeUnit,
    setTimeUnit,
    timeValue,
    setTimeValue,
    schedulingMode,
    setSchedulingMode,
    schedules,
    setSchedules,
    nextRun,
    setNextRun,
    saveBugServiceFrequency
  } = useScheduling();

  async function setBugServiceFrequency() {
    await saveBugServiceFrequency(vendorId);
  }

  async function saveAWSArn() {
    const data = {
      secret: {
        awsApiSecHubApiRoleArn: fields.awsApiSecHubApiRoleArn
      },
      vendorId: 'aws',
      key: 'securityHubArn'
    };
    // cloud apps secret
    return BZAPIService.createOrUpdateAWSSecret(data);
  }

  async function testCreds() {
    setIsTestingValidCredentials(true);
    const data = {
      awsApiSecHubApiRoleArn: fields.awsApiSecHubApiRoleArn
    };
    const result = await BZAPIService.vendorTestCreds(vendorId, data);
    setIsTestingValidCredentials(false);
    if (result) {
      // valid creds
      if (!result.roleAssumed) return toast.error(`Unable to assume role.`);
      if (!result.securityHubApiAccess) return toast.error(`Unable to access AWS Security Hub API.`);

      // setOrgHealthError(result?.healthApiAccessErrorMessage);
      setIsValidCredentials(true);
      // setCustomers([...result.active_iq_customers]);
      setIsAddingCredentials(false);
      setIsUpdatingCreds(true);
      // console.log(result.awsOrgHealth);
      // console.log(result.awsOrgHealthErrorMessage);
      toast.success(`AWS Health API Role ARN validated.`);
    } else {
      toast.error(`Unable to validate AWS Health API Role ARN.`);
    }
  }

  async function saveConfig(serviceSecret) {
    // save client config
    const vendorPriorities = priorityMaps.map(mapping => {
      return {
        value: mapping.vendorPriority.value,
        label: mapping.vendorPriority.value,
        snPriority: mapping.snPriority.value
      };
    });

    const data = {
      bugsDateBack: bugsDateBack,
      secretId: serviceSecret.Name,
      snCompanySysId: snCompanySysId.value ? snCompanySysId.value : fields.snCompanySysId,
      snAffectedCIQuery: fields.snAffectedCIQuery,
      vendorPriorities,
      vendorStatuses,
      type: SETTINGS.VENDOR,
      excludedKws: keywords,
      searchFields: searchFields,
      vendorBugTypes,
      autoCloseBugTicket,
      vendorImpactedRegions,
      vendorComplianceStatus
    };

    return BZAPIService.saveSetting(vendorId, syncOnly, data, isForceRun, isUpdatingCreds);
  }

  function cancelCreds() {
    // fields.awsApiSecHubApiRoleArn = '';
    setIsAddingCredentials(false);
  }

  async function handleSave() {
    try {
      setIsSubmitted(true);
      setIsLoading(true);

      // Validate
      if (!fields.secretId) {
        if (!fields.awsApiSecHubApiRoleArn) {
          console.log('misisng secret');
          setValidated(false);
          setActiveTab('2');
          setIsLoading(false);
          return;
        }
      }
      if (!bugsDateBack.length || !snCompanySysId) {
        console.log('failed validation');
        setValidated(false);
        setActiveTab('3');
        setIsLoading(false);
        return;
      }
      if (!vendorStatuses.length || !priorityMaps.length) {
        console.log('failed validation');
        setValidated(false);
        setActiveTab('4');
        setIsLoading(false);
        return;
      }

      let serviceSecret;

      if (fields.awsApiSecHubApiRoleArn) {
        // Updated secrets
        serviceSecret = await saveAWSArn();
      } else {
        // use existing secrets
        serviceSecret = { Name: fields.secretId };
      }

      if (!serviceSecret || !serviceSecret.Name) {
        setIsLoading(false);
        setValidated(false);
        setActiveTab('2');
        throw Error('Missing Secret');
      }

      await saveConfig(serviceSecret);
      fields.secretId = serviceSecret.Name;
      // set updated secretId
      setBugServiceFrequency();
      setIsLoading(false);
      setIsSubmitted(false);
      setModalOpen(false);
      setIsForceRun(false);
      setIsUpdatingCreds(false);
      setActiveTab('1');
      toast.success(`Settings Saved!`);
    } catch (e) {
      setIsLoading(false);
      toast.error('Failed to save.');
      console.log('e', e);
    }
  }

  function handleValidationError() {
    setModalOpen(!modalOpen);
    animateScroll.scrollTo(70);
    // window.scrollTo(0, 0);
  }

  useEffect(() => {
    setIsUpdated(true);
    if (
      bugsDateBack?.length &&
      snCompanySysId?.value &&
      priorityMaps?.length &&
      !priorityMaps.some(mapping => !mapping.snPriority || !mapping.vendorPriority) &&
      vendorStatuses.length &&
      (fields.secretId || fields.awsApiSecHubApiRoleArn)
    ) {
      setAllRequired(true);
    } else {
      setAllRequired(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, bugsDateBack, priorityStats, vendorStatuses, priorityMaps, keywords, searchFields, snCompanySysId]);

  return (
    <>
      <TabNav
        vendorId={vendorId}
        credentials={true}
        activeTab={activeTab}
        snIntegrationCheck={snIntegrationCheck}
        setActiveTab={setActiveTab}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        setServices={setServices}
        setVendors={setVendors}
        buildVendorStat={buildVendorStat}
        setPriorityStats={setPriorityStats}
        setBugs={setBugs}
        isConfigured={isConfigured}
        loading={loading}
      />

      <TabContent>
        <TabOne
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          snIntegrationCheck={snIntegrationCheck}
          services={services}
          vendors={vendors}
          priorityStats={priorityStats}
          isConfigured={isConfigured}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          setIsForceRun={setIsForceRun}
          timeUnit={timeUnit}
          setTimeUnit={setTimeUnit}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          schedulingMode={schedulingMode}
          setSchedulingMode={setSchedulingMode}
          schedules={schedules}
          setSchedules={setSchedules}
          nextRun={nextRun}
          setNextRun={setNextRun}
          loading={loading}
          isServiceActive={isServiceActive}
          setIsServiceActive={setIsServiceActive}
        />
        <form noValidate style={{ minWidth: '100%' }}>
          <TabPane tabId="2">
            {activeTab === '2' && (
              <Card bg={''} text={'dark'} className="mb-4">
                <CardBody>
                  <Row form>
                    <Col lg="12">
                      <FormGroup>
                        <Label>
                          AWS Security Hub Role ARN
                          <span style={{ color: '#bd472a' }}>*</span>
                        </Label>
                        {!isAddingCredentials ? (
                          <>
                            <div>
                              <ButtonIcon
                                onClick={() => setIsAddingCredentials(true)}
                                color="falcon-default"
                                size="sm"
                                icon="plus"
                                iconClassName="fs--2"
                              >
                                Set Credentials
                              </ButtonIcon>

                              {isValidCredentials || fields.secretId ? (
                                <>
                                  {'  '}
                                  <FontAwesomeIcon icon={'check'} style={{ color: 'green', marginLeft: '10px' }} /> AWS
                                  Security Hub Role ARN Stored
                                </>
                              ) : null}
                            </div>
                          </>
                        ) : (
                          <>
                            <Row>
                              <Col lg="6">
                                <FieldGroup
                                  type="text"
                                  id="awsApiSecHubApiRoleArn"
                                  onChange={handleFieldChange}
                                  value={fields.awsApiSecHubApiRoleArn}
                                  showFeedback={!isValidCredentials}
                                  feedback={'AWS Security Hub Role ARN has not been validated or is invalid.'}
                                  placeholder="Enter AWS Security Hub Role ARN"
                                  style={{ marginBottom: '10px' }}
                                  required
                                />
                              </Col>
                            </Row>
                            <CredsModal
                              isTestingValidCredentials={isTestingValidCredentials}
                              setIsTestingCredentials={setIsTestingValidCredentials}
                              fields={fields}
                              testCreds={testCreds}
                              vendorId={vendorId}
                            />
                            <Button
                              onClick={cancelCreds}
                              color="falcon-danger"
                              size="md"
                              iconclassname="fs--2"
                              style={{ marginRight: '10px' }}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                        <FormFeedback
                          className={!fields.secretId && !fields.awsApiSecHubApiRoleArn && isSubmitted ? 'd-block' : ''}
                          type="invalid"
                        >
                          <Alert color="danger">Please specify credentials.</Alert>
                        </FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}
          </TabPane>

          <DefaultConfigTab
            vendor={vendor}
            activeTab={activeTab}
            vendorLabel={vendorLabel}
            bugsDateBack={bugsDateBack}
            setBugsDateBack={setBugsDateBack}
            isSubmitted={isSubmitted}
            snCompanySysIdErrors={snCompanySysIdErrors}
            snCompanySysIdOptions={snCompanySysIdOptions}
            setSnCompanySysId={setSnCompanySysId}
            snCompanySysId={snCompanySysId}
            fields={fields}
            autoClose={autoCloseBugTicket}
            setAutoClose={setAutoCloseBugTicket}
            handleFieldChange={handleFieldChange}
          />

          <TabPane tabId="4">
            {activeTab === '4' && (
              <>
                <ProductPriority
                  title="Default Bug Configuration"
                  vendorPriorityOptions={vendorPriorityOptions}
                  vendorStatusOptions={vendorStatusOptions}
                  vendorResolutionOptions={vendorResolutionOptions}
                  setVendorStatuses={setVendorStatuses}
                  vendorStatuses={vendorStatuses}
                  isSubmitted={isSubmitted}
                  priorityMaps={priorityMaps}
                  setVendorResolutions={setVendorResolutions}
                  vendorResolutions={vendorResolutions}
                  setPriorityMaps={setPriorityMaps}
                  snPriorityOptions={snPriorityOptions}
                  keywords={keywords}
                  setVendorBugTypes={setVendorBugTypes}
                  vendorBugTypes={vendorBugTypes}
                  vendorBugTypeOptions={vendorBugTypeOptions}
                  setKeywords={setKeywords}
                  searchFields={searchFields}
                  vendorBugCategories={vendorBugCategories}
                  setVendorBugCategories={setVendorBugCategories}
                  setSearchFields={setSearchFields}
                  vendorImpactedRegions={vendorImpactedRegions}
                  setVendorImpactedRegions={setVendorImpactedRegions}
                  vendorImpactedRegionOptions={vendorImpactedRegionOptions}
                  vendorComplianceStatus={vendorComplianceStatus}
                  setVendorComplianceStatus={setVendorComplianceStatus}
                  vendorComplianceStatusOptions={vendorComplianceStatusOptions}
                />
              </>
            )}
          </TabPane>
          <TabPane tabId="6">{activeTab === '6' && <ExecutionStatsTab vendorId={vendorId} />}</TabPane>
          <Row className={`${activeTab === '1' ? 'd-none' : ''} flex-between-center`}>
            <Col xs="auto">
              <Button
                block
                color="falcon-danger"
                onClick={() => {
                  console.log(`/vendors/${vendorId}/configuration`);
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto">
              <SummaryModal
                modalOpen={modalOpen}
                isReadOnly={isReadOnly}
                setModalOpen={setModalOpen}
                handleSave={handleSave}
                isLoading={isLoading}
                vendorLabel={vendorLabel?.replace('Systems', 'Networks')}
                vendorId={vendorId}
                validated={validated}
                setValidated={setValidated}
                syncOnly={syncOnly}
                setSyncOnly={setSyncOnly}
                handleValidationError={handleValidationError}
                allRequired={allRequired}
                isUpdated={isUpdated}
              />
            </Col>
          </Row>
        </form>
      </TabContent>
    </>
  );
};
ConfigFormAwsSecurityHub.propTypes = {
  vendor: PropTypes.object,
  isReadOnly: PropTypes.bool,
  settings: PropTypes.object,
  vendorLabel: PropTypes.string,
  vendorId: PropTypes.string,
  vendorPriorityOptions: PropTypes.array,
  vendorStatusOptions: PropTypes.array,
  vendorResolutionOptions: PropTypes.array,
  vendorEventScopeOptions: PropTypes.array,
  snCompanySysIdOptions: PropTypes.object,
  snCompanySysId: PropTypes.object,
  snIntegrationCheck: PropTypes.bool,
  services: PropTypes.array,
  vendors: PropTypes.array,
  bugs: PropTypes.object,
  priorityStats: PropTypes.object,
  setPriorityStats: PropTypes.func,
  bugOptions: PropTypes.array,
  selectedBugOption: PropTypes.object,
  setSelectedBugOption: PropTypes.func,
  snCompanySysIdErrors: PropTypes.bool,
  vendorResolutions: PropTypes.array,
  setVendorResolutions: PropTypes.func,
  setVendorStatuses: PropTypes.func,
  vendorStatuses: PropTypes.array,
  fields: PropTypes.object,
  handleFieldChange: PropTypes.func,
  priorityMaps: PropTypes.array,
  setPriorityMaps: PropTypes.func,
  snPriorityOptions: PropTypes.array,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  setSnCompanySysId: PropTypes.func,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  setServices: PropTypes.func,
  setVendors: PropTypes.func,
  buildVendorStat: PropTypes.func,
  setBugs: PropTypes.func,
  isConfigured: PropTypes.bool,
  syncOnly: PropTypes.bool,
  setSyncOnly: PropTypes.func,
  loading: PropTypes.bool,
  setIsServiceActive: PropTypes.func,
  vendorBugTypes: PropTypes.array,
  setVendorBugTypes: PropTypes.func,
  isServiceActive: PropTypes.number,
  organizations: PropTypes.array,
  vendorBugCategories: PropTypes.array,
  vendorBugCategoryOptions: PropTypes.array,
  vendorBugTypeOptions: PropTypes.array,
  vendorImpactedRegions: PropTypes.array,
  setVendorImpactedRegions: PropTypes.func,
  vendorImpactedRegionOptions: PropTypes.array,
  vendorComplianceStatus: PropTypes.array,
  setVendorComplianceStatus: PropTypes.func,
  vendorComplianceStatusOptions: PropTypes.array,
  setVendorBugCategories: PropTypes.func,
  setAutoCloseBugTicket: PropTypes.func,
  autoCloseBugTicket: PropTypes.bool,
  setOrganizations: PropTypes.func,
  organizationOptions: PropTypes.array,
  setOrganizationOptions: PropTypes.func,
  awsOrgHealthEnabled: PropTypes.bool,
  setAwsOrgHealthEnabled: PropTypes.func,
  awsOrgHealthSupport: PropTypes.bool,
  allRequired: PropTypes.bool,
  isUpdated: PropTypes.bool,
  setAllRequired: PropTypes.func,
  setIsUpdated: PropTypes.func
};
export default ConfigFormAwsSecurityHub;
